'use client';

import { type Dispatch, useReducer } from 'react';

import { type VideoEditSubmitAction, type VideoEditSubmitState } from '../types';

export const VIDEO_EDIT_SUBMIT_DEFAULTS: VideoEditSubmitState = {
    pendingTaskId: null,
    originalVideoUrl: '',
    originalThumbnailUrl: '',
    duration: undefined,
    aspectRatio: undefined,
    contentId: null,
    editedVideoUrl: '',
    aiContentId: '',
    lastAiContentId: '',
    aiContentType: null,
    aiModelName: null,
    status: 'idle',
    restoredFromHistory: false,
    failureReason: null,
    lastCompletedEdit: null,
};

export function videoEditSubmitReducer(
    state: VideoEditSubmitState,
    action: VideoEditSubmitAction,
): VideoEditSubmitState {
    switch (action.type) {
        case 'SELECTION':
            return {
                ...state,
                status: 'selection',
                editedVideoUrl: '',
                originalVideoUrl: action.originalVideoUrl,
                originalThumbnailUrl: action.originalThumbnailUrl,
                duration: action.duration,
                aspectRatio: action.aspectRatio,
                contentId: action.contentId,
                startTime: undefined,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'GENERATION_IN_PROGRESS':
            if (state.status === 'inProgress' && !action.pendingTaskId) return state;
            return {
                ...state,
                status: 'inProgress',
                editedVideoUrl: '',
                restoredFromHistory: false,
                pendingTaskId: action.pendingTaskId ?? null,
                aiContentId: action.aiContentId ?? state.aiContentId,
                lastAiContentId: action.lastAiContentId ?? state.lastAiContentId,
                aiContentType: action.aiContentType ?? state.aiContentType,
                aiModelName: action.aiModelName ?? state.aiModelName,
                startTime: action.startTime ?? state.startTime,
                duration: action.duration ?? state.duration,
                contentId: action.contentId ?? state.contentId,
                failureReason: null,
            };
        case 'GENERATION_SUCCESS': {
            const aiContentType = action.aiContentType ?? state.aiContentType;
            const aiModelName = action.aiModelName ?? state.aiModelName;
            return {
                ...state,
                status: 'success',
                editedVideoUrl: action.editedVideoUrl,
                aiContentId: action.aiContentId,
                lastAiContentId: action.lastAiContentId,
                aiContentType,
                aiModelName,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
                lastCompletedEdit: {
                    editedVideoUrl: action.editedVideoUrl,
                    originalVideoUrl: state.originalVideoUrl,
                    originalThumbnailUrl: state.originalThumbnailUrl,
                    aiContentId: action.aiContentId,
                    lastAiContentId: action.lastAiContentId,
                    aiContentType,
                    aiModelName,
                    startTime: state.startTime,
                    duration: state.duration,
                    contentId: state.contentId,
                    aspectRatio: state.aspectRatio,
                },
            };
        }
        case 'GENERATION_ERROR':
            // The selected source video survives so the user can retry without re-selecting it.
            return {
                ...state,
                status: 'error',
                editedVideoUrl: '',
                restoredFromHistory: false,
                pendingTaskId: null,
                aiContentType: null,
                aiModelName: null,
                failureReason: action.failureReason ?? null,
            };
        case 'RESTORE_LAST_COMPLETED': {
            const snapshot = state.lastCompletedEdit;
            if (!snapshot) return state;
            return {
                ...state,
                ...snapshot,
                status: 'success',
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        }
        case 'EDIT_SELECTION':
            return {
                ...state,
                status: 'selection',
                editedVideoUrl: '',
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'RESET':
            return VIDEO_EDIT_SUBMIT_DEFAULTS;
    }
}

export function useVideoEditSubmit(
    initialState: VideoEditSubmitState = VIDEO_EDIT_SUBMIT_DEFAULTS,
): [VideoEditSubmitState, Dispatch<VideoEditSubmitAction>] {
    return useReducer(videoEditSubmitReducer, initialState);
}
